import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { getCookie, deleteCookie, setCookie } from './api/cookie';

// Define a type for your context state
interface AuthContextType {
  email: string;
  setEmail: (email: string) => void;
  isAuthenticated: boolean;
  token: string | null;
  user: any | null;
  displayPhoneNumber: string | null;
  setDisplayPhoneNumber: (phoneNumber: string | null) => void;
  verifiedName: string | null;
  setVerifiedName: (name: string | null) => void;
  login: (token: string, userData?: any) => void;
  logout: () => void;
}

// Create the context with an initial undefined value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  console.log('AuthProvider rendering...');
  
  // Initialize auth state synchronously to prevent race conditions on page refresh
  const existingToken = getCookie('token');
  const existingEmail = localStorage.getItem('email');
  const existingDisplayPhone = sessionStorage.getItem('display_phone_number');
  const existingVerifiedName = sessionStorage.getItem('verified_name');
  const sessionPhone = sessionStorage.getItem('phone');
  
  // Set initial authenticated state synchronously
  const initialIsAuthenticated = !!(existingToken || sessionPhone);
  
  const [email, setEmail] = useState<string>(existingEmail || sessionPhone || '');
  const [token, setToken] = useState<string | null>(existingToken || null);
  const [user, setUser] = useState<any | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(initialIsAuthenticated);
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState<string | null>(existingDisplayPhone);
  const [verifiedName, setVerifiedName] = useState<string | null>(existingVerifiedName);

  // Check for existing token on mount (for any additional initialization)
  useEffect(() => {
    console.log('AuthContext: Initializing authentication...');
    
    console.log('AuthContext: Found data:', {
      existingToken: !!existingToken,
      existingEmail,
      existingDisplayPhone,
      existingVerifiedName,
      sessionPhone
    });
    
    if (existingToken && !token) {
      console.log('AuthContext: Using existing token');
      setToken(existingToken);
      setIsAuthenticated(true);
    } else if (sessionPhone && !isAuthenticated) {
      // Fallback: if we have phone in sessionStorage, consider user authenticated
      console.log('AuthContext: Using session phone as fallback');
      setIsAuthenticated(true);
      if (!email) {
        setEmail(sessionPhone);
      }
    }
    
    if (existingEmail && !email) {
      setEmail(existingEmail);
    }
    
    if (existingDisplayPhone && !displayPhoneNumber) {
      setDisplayPhoneNumber(existingDisplayPhone);
    }
    
    if (existingVerifiedName && !verifiedName) {
      setVerifiedName(existingVerifiedName);
      console.log('AuthContext: Loaded verified name:', existingVerifiedName);
    }
    
    console.log('AuthContext: Final state:', {
      isAuthenticated: initialIsAuthenticated,
      email: existingEmail || sessionPhone,
      displayPhoneNumber: existingDisplayPhone,
      verifiedName: existingVerifiedName
    });
  }, []);

  const login = (newToken: string, userData?: any) => {
    console.log('AuthContext: Login called with:', { newToken: !!newToken, userData });
    setToken(newToken);
    setIsAuthenticated(true);
    
    // Set the token cookie
    setCookie('token', newToken, 7); // 7 days expiry
    console.log('AuthContext: Token cookie set');
    
    if (userData) {
      setUser(userData);
      // Set user data cookies
      if (userData.phoneNumber) {
        setCookie('user_email', userData.phoneNumber, 7);
        setCookie('user_name', userData.phoneNumber, 7);
        console.log('AuthContext: User data cookies set');
      }
    }
    
    console.log('AuthContext: Login completed, isAuthenticated:', true);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setEmail('');
    deleteCookie('token');
    deleteCookie('user_email');
    deleteCookie('user_id');
    deleteCookie('user_name');
    
    // Clear sensitive data from sessionStorage (temporary data)
    sessionStorage.removeItem('phone');
    sessionStorage.removeItem('logintype');
    sessionStorage.removeItem('display_phone_number');
    sessionStorage.removeItem('verified_name');
    sessionStorage.removeItem('wabaId');
    sessionStorage.removeItem('phoneNumberId');
    
    // Clear email from localStorage (user identifier - standard practice)
    localStorage.removeItem('email');
  };

  return (
    <AuthContext.Provider value={{ 
      email, 
      setEmail, 
      isAuthenticated, 
      token, 
      user, 
      displayPhoneNumber,
      setDisplayPhoneNumber,
      verifiedName,
      setVerifiedName,
      login, 
      logout 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
